import _ from "lodash";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classes from "./WorkbookTemplate.scss";
import { getWorkbookTemplateQueryV2 } from "./modules/WorkbookTemplateQueries";
import { getWorkbookTemplatesFromCache } from "./modules/WorkbookTemplateGraphqlHelpers";
import { resourceLibrarySourceOrg } from "config";
import classNames from "classnames";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import CarouselCards from "./components/CarouselCards";
import {
  Button,
  SearchInput,
  IconButton,
  SideNavigation,
  EmptyState,
} from "@toddle-edu/ds-web";

import { CloseOutlined } from "@toddle-edu/ds-icons";

import Workbook from "AppComponents/Workbook";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import PaginationLoader from "UIComponents/PaginationLoader";
import ACLStore from "lib/aclStore";

const DEFAULT_RIGHT_PADDING = 32;

const WorkbookTemplate = React.memo(props => {
  const {
    closeModal,
    t,
    templateData,
    onUpdateSuccessfully,
    updateAttachments,
    isLoading,
    isData,
    isCreateBlankWorkbookAttachment,
    updateWorkbookTemplateFilters,
    templateAttachment,
    setTemplateAttachment,
    searchText,
    folderId,
  } = props;
  const [selectedOption, setSelectedOption] = useState("allTemplates");
  const [selectedTemplate, setSelectedTemplate] = useState(["Blank workbook"]);
  const [showWorkbookPreview, setShowWorkbookPreview] = useState(false);
  const [showPagniationLoader, setShowPagniationLoader] = useState(false);
  const [scrollBarWidth, setScrollBarWidth] = useState(0);
  const scrollRef = useRef(null);
  const parentRef = useRef(null);

  useEffect(() => {
    if (
      isCreateBlankWorkbookAttachment &&
      ACLStore.can("FeatureFlag:WorkbookTemplatesCreateBlankDebug") &&
      !isLoading &&
      !isData
    ) {
      const { createBlankWorkbook } = props;
      createBlankWorkbook?.();
    }
    getScrollbarWidth();
  }, [isLoading, isData, isCreateBlankWorkbookAttachment]);

  const resetScroll = useCallback(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scrollRef]);

  const updateFolderId = useCallback(
    folderId => {
      resetScroll();
      setSelectedOption(folderId);
      updateWorkbookTemplateFilters({ key: "folderId", value: folderId });
    },
    [resetScroll, updateWorkbookTemplateFilters]
  );

  const searchBoxClass = classNames({
    [classes.searchBox]: true,
    ["body-1"]: true,
  });

  const sideBarOptions = useMemo(() => {
    const totalTemplates = () => {
      let add = 0;
      _.map(templateData, template => {
        const tempLength = _.get(
          template.childrenV2.resourceTypeBasedCount,
          "totalCount",
          0
        );
        add = add + tempLength;
      });
      return `(${add})`;
    };

    const options = [
      {
        key: "root",
        children: ["1", "2"],
      },
      {
        key: "1",
        tier: 3,
        parent: "root",
        children: ["allTemplates", "divider"],
      },
      {
        key: "allTemplates",
        title: (
          <div className={classes.templateLabel}>
            <div>{"All Templates"}</div>
            <div className={classes.templateCount}>{totalTemplates()}</div>
          </div>
        ),
        tier: 1,
        parent: "1",
        children: [],
      },
      {
        key: "divider",
        isDivider: true,
      },
      {
        key: "2",
        title: "",
        tier: 3,
        parent: "root",
        children: ["options"],
      },
      {
        key: "options",
        tier: 2,
        parent: "2",
        children: [],
      },
    ];

    const rootChildrenArray = _.get(options, ["5", "children"]);

    _.map(templateData, template => {
      const { label, id } = template;
      rootChildrenArray.push(id);
      const titleAndCount = (
        <div className={classes.templateLabel}>
          <div>{label}</div>
          <div className={classes.templateCount}>{`(${_.get(
            template.childrenV2.resourceTypeBasedCount,
            "totalCount"
          )})`}</div>
        </div>
      );
      const option = {
        key: id,
        title: titleAndCount,
        tier: 1,
        parent: "2",
        children: [],
      };
      options.push(option);
    });

    return options;
  }, [templateData]);

  const checkIfTemplateHasData = () => {
    let hasData = false;
    let hasSearchData = false;

    _.map(templateData, template => {
      if (
        _.get(template, "childrenV2.resourceTypeBasedCount.totalCount", 0) > 0
      ) {
        hasSearchData = true;
        hasData = hasData || hasSearchData;
      }
    });
    return hasData;
  };

  const renderCards = () => {
    return folderId == "allTemplates" ? (
      _.map(templateData, template => {
        const { id } = template;
        return (
          <CarouselCards
            template={template}
            setSelectedTemplate={setSelectedTemplate}
            setTemplateAttachment={setTemplateAttachment}
            selectedTemplate={selectedTemplate}
            updateFolderId={updateFolderId}
            sideBarOptions={sideBarOptions}
            scrollRef={scrollRef}
            searchText={searchText}
            folderId={folderId}
            id={id}
            t={t}
          />
        );
      })
    ) : (
      <CarouselCards
        setSelectedTemplate={setSelectedTemplate}
        setTemplateAttachment={setTemplateAttachment}
        selectedTemplate={selectedTemplate}
        updateFolderId={updateFolderId}
        sideBarOptions={sideBarOptions}
        scrollRef={scrollRef}
        setShowPagniationLoader={setShowPagniationLoader}
        searchText={searchText}
        folderId={folderId}
        t={t}
      />
    );
  };

  const renderWorkbook = () => {
    return (
      showWorkbookPreview && (
        <Workbook
          shouldEnablePreload={false}
          imageOrientation={0}
          attachment={templateAttachment}
          workbookMode={"WORKBOOK_TEMPLATE_PREVIEW"}
          isFullScreen={false}
          onUpdateSuccessfully={onUpdateSuccessfully}
          onDiscardDirectly={closeModal}
          updateAttachments={updateAttachments}
          iframeIdSelector={"workbook-iframe-classroom-1"}
          parentRef={parentRef?.current}
          parentId={"workbook-iframe-classroom-2"}
          portalType={"RESOURCE_LIBRARY"}
        />
      )
    );
  };

  const onClickUseTemplate = () => {
    const { createBlankWorkbook, handleCreateAttachment } = props;

    const isCurrentFolderBlankWorkbook =
      _.isEqual(selectedOption, "allTemplates") ||
      _.get(
        _.find(templateData, data => _.get(data, "id") == selectedOption, {}),
        "label"
      ) == "Basic";

    if (isCurrentFolderBlankWorkbook) {
      const templateAttachmentsArray = [];
      _.map(templateData, template => {
        const edgesArray = _.get(template, "childrenV2.edges", []);
        _.map(edgesArray, edge => {
          const edgeNode = _.get(edge, "node");
          if (!_.isEmpty(edgeNode)) {
            templateAttachmentsArray.push({
              id: edgeNode.id,
              label: edgeNode.label,
            });
          }
        });
      });

      const isBlankWorkook =
        _.toLower(
          _.get(
            _.find(
              templateAttachmentsArray,
              ({ id }) => id == selectedTemplate
            ),
            "label"
          )
        ) == _.toLower("Blank Workbook");

      if (isBlankWorkook) {
        createBlankWorkbook();
        return;
      }
    }

    handleCreateAttachment();
  };

  const updateSearchText = _.debounce(
    val =>
      updateWorkbookTemplateFilters({
        key: "searchText",
        value: val,
      }),
    500
  );
  const getScrollbarWidth = () => {
    if (scrollRef.current) {
      setScrollBarWidth(
        scrollRef.current.offsetWidth - scrollRef.current.clientWidth
      );
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className="heading-4">{"Choose a template"}</div>
        <div className={classes.headerRightContainer}>
          {!showWorkbookPreview ? (
            <div className={classes.headerSearch}>
              <SearchInput
                placeholder={`${t("common:search_for_with_label", {
                  label: t("common:template_plural"),
                })}`}
                onChange={val => updateSearchText(val)}
                size={"medium"}
              />
            </div>
          ) : null}
          <IconButton
            icon={<CloseOutlined />}
            size={"medium"}
            variant={"plain"}
            onClick={closeModal}
            testId={"workbook-workbookTemplate-closeModal"}
          />
        </div>
      </div>
      <div
        className={classes.centerContainer}
        ref={parentRef}
        id={"workbook-iframe-classroom-2"}
      >
        {showWorkbookPreview ? (
          renderWorkbook()
        ) : (
          <div className={classes.centerDataContainer}>
            <div className={classes.leftContainer}>
              <SideNavigation
                options={sideBarOptions}
                onClick={updateFolderId}
                selectedOptionKey={selectedOption}
                testId={"workbook-workbookTemplate-updateFolderId"}
              />
            </div>
            <div
              className={classes.bodyContainer}
              ref={scrollRef}
              style={{
                paddingRight: `${DEFAULT_RIGHT_PADDING - scrollBarWidth}`,
              }}
            >
              {!_.isEmpty(searchText) ? (
                <div className={searchBoxClass}>
                  {t("common:search_result", {
                    searchText,
                  })}
                </div>
              ) : null}
              {checkIfTemplateHasData() ? (
                <>
                  {renderCards()}
                  {showPagniationLoader ? <PaginationLoader /> : null}
                </>
              ) : isLoading ? (
                <Loading />
              ) : !isData ? (
                <EmptyState
                  illustration={NoSearchResultsIllustration}
                  title={t("common:noResultHeading")}
                />
              ) : (
                <EmptyState
                  illustration={NoSearchResultsIllustration}
                  title={t("common:noResultHeading")}
                  subtitle={t("common:no_posts_matching_your_search")}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <div className={classes.footerContainer}>
        <div className={classes.footerLeftContainer}>
          {showWorkbookPreview ? (
            <Button
              variant={"outlined-subtle"}
              onClick={() => setShowWorkbookPreview(false)}
              testId={"workbook-workbookTemplate-hideWorkbook"}
            >
              {t("common:back")}
            </Button>
          ) : (
            <Button
              variant={"inline-progressive"}
              size={"large"}
              onClick={() => setShowWorkbookPreview(true)}
              disabled={_.isEmpty(templateAttachment)}
              testId={"workbook-workbookTemplate-previewWorkbook"}
            >
              {t("common:preview")}
            </Button>
          )}
        </div>
        <div className={classes.footerRightContainer}>
          {!showWorkbookPreview ? (
            <Button
              variant={"outlined-subtle"}
              size={"large"}
              onClick={closeModal}
              testId={"workbook-workbookTemplate-close"}
            >
              {t("common:cancel")}
            </Button>
          ) : null}

          <Button
            variant={"progressive"}
            size={"large"}
            onClick={onClickUseTemplate}
            disabled={_.isEmpty(templateAttachment)}
            testId={"workbook-workbookTemplate-useTemplate"}
          >
            {t("classRoom:use_template")}
          </Button>
        </div>
      </div>
    </div>
  );
});

WorkbookTemplate.displayName = "WorkbookTemplate";

WorkbookTemplate.propTypes = {};

const templateFilters = {
  id: _.get(resourceLibrarySourceOrg, "workbook"),
  types: ["WORKBOOK_TEMPLATE"],
  portalType: "RESOURCE_LIBRARY",
  first: 10,
  states: ["PUBLISHED"],
  courseIds: [],
  createdBy: [],
};

const mapStateToProps = (state, ownProps) => {
  const searchText = _.get(ownProps, "searchText", "");
  const folderId = _.get(ownProps, "folderId", "");
  return {
    searchText,
    templateFilters: {
      ...templateFilters,
      searchText,
    },
    folderId,
    isLoading: false,
    isData: true,
  };
};

export default compose(
  I18nHOC({ resource: ["common", "teacherHomePage"] }),
  connect(mapStateToProps, null),
  graphql(getWorkbookTemplateQueryV2, {
    name: "getWorkbookTemplates",
    options: ({ templateFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...templateFilters,
      },
    }),
    props({
      getWorkbookTemplates,
      ownProps: { isData, isLoading, templateFilters },
    }) {
      const data = getWorkbookTemplatesFromCache({
        ...templateFilters,
      });

      const templateData = _.get(data, "node.organizationResources", []);

      let isCreateBlankWorkbookAttachment = false;
      if (
        _.isEmpty(templateData) &&
        _.isEmpty(_.get(templateFilters, "searchText"))
      ) {
        isCreateBlankWorkbookAttachment = true;
      }

      return {
        templateData,
        isData: !_.isEmpty(data) && isData,
        isCreateBlankWorkbookAttachment,
        isLoading:
          getWorkbookTemplates["networkStatus"] == 1 ||
          getWorkbookTemplates["networkStatus"] == 2 ||
          isLoading,
      };
    },
  })
)(WorkbookTemplate);
